import type { PillVariant } from "@atlas/ui/web";
import type { EstadoRefinanciamento, Refinanciamento } from "@atlas/sdk";

export const ESTADO_REFIN_LABEL: Record<EstadoRefinanciamento, string> = {
  solicitado: "Aguardando a instituição",
  ofertado: "Proposta da instituição",
  aceito: "Aceito — contrato em formalização",
  efetivado: "Efetivado em folha",
  recusado: "Recusado",
  cancelado: "Cancelado",
  expirado: "Expirado",
};

/** Mesma leitura, do lado de quem opera o banco ou a averbadora. */
export const ESTADO_REFIN_LABEL_OPERADOR: Record<EstadoRefinanciamento, string> = {
  solicitado: "Pedido do servidor",
  ofertado: "Aguardando o servidor",
  aceito: "Aceito — sucessor em formalização",
  efetivado: "Efetivado",
  recusado: "Recusado",
  cancelado: "Cancelado",
  expirado: "Expirado",
};

export function pillDoEstadoRefin(estado: EstadoRefinanciamento): PillVariant {
  switch (estado) {
    case "solicitado":
    case "ofertado":
      return "pendente";
    case "aceito":
      return "aceita";
    case "efetivado":
      return "averbado";
    case "recusado":
      return "rejeitada";
    default:
      return "expirado";
  }
}

export function refinEmAberto(r: Pick<Refinanciamento, "estado">): boolean {
  return r.estado === "solicitado" || r.estado === "ofertado";
}

/** O que cabe a quem esta' olhando o refinanciamento agora. */
export function acoesDoRefin(
  r: Pick<Refinanciamento, "estado" | "iniciadoPor">,
  papel: "servidor" | "banco",
): { aceitar: boolean; recusar: boolean; responder: boolean; cancelar: boolean } {
  if (papel === "servidor") {
    return {
      aceitar: r.estado === "ofertado",
      recusar: r.estado === "ofertado",
      responder: false,
      cancelar: r.estado === "solicitado",
    };
  }
  return {
    aceitar: false,
    recusar: false,
    responder: r.estado === "solicitado",
    // Aceito ja' tem sucessor: desistir e' cancelar o contrato, e o refin segue.
    cancelar: r.estado === "solicitado" || r.estado === "ofertado",
  };
}

/**
 * Selecao de origens no pedido: todas do mesmo credor, e mais de uma so' quando
 * o convenio permite unificar. Marcar um contrato de outro banco recomeca a
 * selecao por ele, em vez de montar um pedido que a API recusaria.
 */
export function alternarOrigem<T extends { adf: string; bancoId: number; regras: { permiteUnificar: boolean } }>(
  selecionadas: readonly string[],
  item: T,
  todos: readonly T[],
): string[] {
  if (selecionadas.includes(item.adf)) return selecionadas.filter((a) => a !== item.adf);
  const primeira = todos.find((t) => t.adf === selecionadas[0]);
  if (!primeira || primeira.bancoId !== item.bancoId || !item.regras.permiteUnificar) return [item.adf];
  return [...selecionadas, item.adf];
}

/** Contratos REFIN que nasceram de um refinanciamento com o proprio credor. */
export function adfsSucessorasDeRefin(itens: readonly Pick<Refinanciamento, "adfSucessor">[]): Set<string> {
  return new Set(itens.map((r) => r.adfSucessor).filter((a): a is string => !!a));
}

export function fmtTaxaFracao(taxa: number): string {
  return `${(taxa * 100).toFixed(2).replace(".", ",")}% a.m.`;
}

/** "2026-09-30T12:00:00Z" -> "30/09/2026 09:00" no fuso do navegador. */
export function fmtDataHora(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}
